import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ColorMode } from './appColorTokens';
import { createAppTheme } from './createAppTheme';

const STORAGE_KEY = 'urace-color-mode';

interface ColorModeContextValue {
  mode: ColorMode;
  toggleMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({ mode: 'light', toggleMode: () => {} });

// Saved choice wins; otherwise follow the OS preference.
const readInitialMode = (): ColorMode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Storage blocked (private mode); fall through to the OS preference.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ColorMode>(readInitialMode);
  const { i18n } = useTranslation();

  // index.css keys its CSS variables off this attribute.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Non-fatal: the mode just won't persist.
    }
  }, [mode]);

  const toggleMode = useCallback(() => setMode(m => (m === 'light' ? 'dark' : 'light')), []);
  const theme = useMemo(() => createAppTheme(mode, i18n.language), [mode, i18n.language]);
  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
